import React, { useEffect, useState } from 'react';

// Admin → Trials: who's on a trial now (7-day card trials from sign-up and
// free trials granted by hand), days left and how much they've used it,
// plus trials that ended in the last 30 days and whether they converted.
// Data: GET /api/admin/trials.

interface Trial {
  userId: number; email: string; name: string; company: string | null; tierName: string | null;
  kind: 'card' | 'free'; cancelling: boolean; endsAt: string | null; daysLeft: number | null;
  signedUp: string | null; lastLogin: string | null; teamSize: number; projects: number; peopleImported: number;
}
interface EndedTrial { userId: number; email: string; name: string; company: string | null; tierName: string | null; endedAt: string; outcome: string }

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');
const ago = (iso: string | null) => {
  if (!iso) return 'never';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
};

export const TrialsPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [trials, setTrials] = useState<Trial[] | null>(null);
  const [ended, setEnded] = useState<EndedTrial[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/admin/trials', { headers: { Authorization: `Bearer ${window.localStorage.getItem('token')}` } })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) { setError(d.error || 'Failed to load trials'); return; }
        setTrials(d.trials || []);
        setEnded(d.ended || []);
      })
      .catch(() => setError('Failed to load trials'));
  }, []);

  const endingSoon = (trials || []).filter((t) => t.daysLeft !== null && t.daysLeft <= 2).length;

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <button onClick={() => setOpen(!open)} className="w-full p-6 flex justify-between items-center text-left">
        <div>
          <h2 className="text-xl font-semibold">
            Trials{trials ? ` (${trials.length})` : ''}
            {endingSoon > 0 && <span className="ml-2 text-xs font-medium bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 align-middle">{endingSoon} ending within 2 days</span>}
          </h2>
          <p className="text-sm text-gray-500 mt-1">Who's on a free trial now, days left and how much they've used it — and recent trials that converted or not</p>
        </div>
        <span className="text-sm text-blue-600">{open ? 'Hide' : 'Open'}</span>
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-6">
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">On a trial now ({trials ? trials.length : '…'})</h3>
            {trials && trials.length === 0 ? <p className="text-sm text-gray-500">Nobody right now.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
                      <th className="py-2 pr-4">Account</th><th className="pr-4">Plan</th><th className="pr-4">Days left</th>
                      <th className="pr-4">Signed up</th><th className="pr-4">Last login</th><th className="pr-4">Projects</th><th className="pr-4">People imported</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(trials || []).map((t) => (
                      <tr key={t.userId} className="border-b border-gray-100 align-top">
                        <td className="py-2 pr-4">
                          <div className="font-medium text-gray-900">{t.name || t.email}</div>
                          <div className="text-xs text-gray-500">{t.email}{t.company ? ` · ${t.company}` : ''}{t.teamSize > 1 ? ` · team of ${t.teamSize}` : ''}</div>
                        </td>
                        <td className="pr-4">
                          {t.tierName || '—'}
                          <div className="text-xs text-gray-500">{t.kind === 'card' ? '7-day card trial' : 'Free trial (granted)'}</div>
                        </td>
                        <td className="pr-4 whitespace-nowrap">
                          <span className={`font-semibold tabular-nums ${t.daysLeft !== null && t.daysLeft <= 2 ? 'text-amber-700' : 'text-gray-900'}`}>
                            {t.daysLeft === null ? '—' : t.daysLeft === 0 ? 'Ends today' : `${t.daysLeft} day${t.daysLeft === 1 ? '' : 's'}`}
                          </span>
                          <div className="text-xs text-gray-500">ends {day(t.endsAt)}</div>
                          {t.cancelling && <div className="text-xs text-red-600">Cancelled — won't convert</div>}
                        </td>
                        <td className="pr-4 text-gray-600 whitespace-nowrap">{day(t.signedUp)}</td>
                        <td className={`pr-4 whitespace-nowrap ${!t.lastLogin ? 'text-red-600' : 'text-gray-600'}`}>{ago(t.lastLogin)}</td>
                        <td className="pr-4 tabular-nums">{t.projects}</td>
                        <td className="pr-4 tabular-nums">{t.peopleImported.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Ended in the last 30 days ({ended.length})</h3>
            {ended.length === 0 ? <p className="text-sm text-gray-500">None.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
                      <th className="py-2 pr-4">Account</th><th className="pr-4">Plan</th><th className="pr-4">Ended</th><th className="pr-4">Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ended.map((t) => (
                      <tr key={t.userId} className="border-b border-gray-100">
                        <td className="py-2 pr-4">
                          <div className="font-medium text-gray-900">{t.name || t.email}</div>
                          <div className="text-xs text-gray-500">{t.email}{t.company ? ` · ${t.company}` : ''}</div>
                        </td>
                        <td className="pr-4">{t.tierName || '—'}</td>
                        <td className="pr-4 whitespace-nowrap text-gray-600">{day(t.endedAt)}</td>
                        <td className="pr-4">
                          <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${t.outcome === 'converted' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                            {t.outcome === 'converted' ? 'Converted — paying' : t.outcome === 'not converted' ? 'Didn’t convert' : t.outcome}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
